import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Phone, Mail, MapPin, Clock, MessageCircle,
  Instagram, Facebook, Store,
} from 'lucide-react';
import { contactAPI } from '../api/contact';
import { logoAPI, resolveLogoUrl } from '../api/logo';

function ContactItem({ icon: Icon, label, value, sub, href, idx }) {
  const inner = (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.05 }}
      className="flex items-center gap-4 rounded-2xl p-4 border"
      style={{
        background: 'rgba(255,255,255,0.05)',
        borderColor: 'rgba(212,160,23,0.18)',
      }}
    >
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: 'linear-gradient(135deg, #d4a017, #f0c040)' }}
      >
        <Icon size={17} color="#fff" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-medium" style={{ color: 'rgba(255,255,255,0.4)' }}>
          {label}
        </p>
        <p className="text-sm font-semibold text-white break-words">{value}</p>
        {sub && (
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.38)' }}>{sub}</p>
        )}
      </div>
    </motion.div>
  );

  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
        {inner}
      </a>
    );
  }
  return inner;
}

export default function ContactPage() {
  const navigate = useNavigate();
  const [info,    setInfo]    = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      contactAPI.get().then(({ data }) => { if (data.status) setInfo(data.data); }).catch(() => {}),
      logoAPI.get().then(({ data }) => { if (data.status && data.data) setLogoUrl(resolveLogoUrl(data.data.logo_url)); }).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  // Build the list of items from fetched data — only show rows that have a value
  const items = info ? [
    info.phone     && { icon: Phone,          label: 'Phone',         value: info.phone,     sub: info.store_hours || undefined, href: `tel:${info.phone}` },
    info.whatsapp  && { icon: MessageCircle,  label: 'WhatsApp',      value: info.whatsapp,  sub: 'Quick response on WhatsApp',  href: `https://wa.me/${info.whatsapp.replace(/\D/g, '')}` },
    info.email     && { icon: Mail,           label: 'Email',         value: info.email,     sub: 'We reply within 24 hours',    href: `mailto:${info.email}` },
    info.address   && { icon: MapPin,         label: 'Store Address', value: info.address,   sub: info.store_hours || undefined, href: `https://maps.google.com/?q=${encodeURIComponent(info.address)}` },
    info.instagram && { icon: Instagram,      label: 'Instagram',     value: info.instagram, sub: 'Follow us for updates',       href: info.instagram },
    info.facebook  && { icon: Facebook,       label: 'Facebook',      value: info.facebook,  sub: 'Like our page for offers',    href: info.facebook },
  ].filter(Boolean) : [];

  return (
    <div className="app-shell flex flex-col min-h-screen">

      {/* Sticky Header */}
      <div
        className="sticky top-0 z-20 flex items-center gap-3 px-4 py-4 border-b"
        style={{
          background: 'rgba(15,27,80,0.95)',
          backdropFilter: 'blur(12px)',
          borderColor: 'rgba(212,160,23,0.15)',
        }}
      >
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: 'rgba(255,255,255,0.08)' }}
        >
          <ArrowLeft size={18} color="rgba(255,255,255,0.8)" />
        </button>
        <div>
          <h1 className="text-base font-serif font-bold text-white">Contact Us</h1>
          <p className="text-[11px]" style={{ color: 'rgba(255,255,255,0.4)' }}>
            Shine Jewellery — We're here to help
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col gap-3 px-4 py-5">
          <div className="skeleton h-24 rounded-3xl" />
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="skeleton h-16 rounded-2xl" />
          ))}
        </div>
      ) : (
        <>
          {/* Hero */}
          <div className="px-4 pt-5 pb-2">
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-3xl px-5 py-5 border"
              style={{
                background: 'linear-gradient(135deg, rgba(212,160,23,0.15) 0%, rgba(240,192,64,0.08) 100%)',
                borderColor: 'rgba(212,160,23,0.25)',
              }}
            >
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3 overflow-hidden"
                style={{ background: 'linear-gradient(135deg, #d4a017, #f0c040)' }}
              >
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo" className="w-full h-full object-contain p-1" />
                ) : (
                  <Store size={20} color="#fff" />
                )}
              </div>
              <p className="font-serif font-bold text-white text-lg">
                {info?.business_name || 'Shine Jewellery'}
              </p>
              <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.55)' }}>
                Premium Gold Investment Platform
              </p>
              {info?.store_hours && (
                <div className="flex items-center gap-1.5 mt-3">
                  <Clock size={12} color="#f0c040" />
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>
                    {info.store_hours}
                  </p>
                </div>
              )}
            </motion.div>
          </div>

          {/* Contact Items */}
          {items.length > 0 ? (
            <div className="flex flex-col gap-3 px-4 py-4">
              {items.map((item, idx) => (
                <ContactItem key={item.label} {...item} idx={idx} />
              ))}
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center px-4">
              <p className="text-sm text-center" style={{ color: 'rgba(255,255,255,0.35)' }}>
                Contact details will appear here once added by the admin.
              </p>
            </div>
          )}
        </>
      )}

      {/* Footer */}
      <div
        className="mt-auto px-4 py-6 border-t text-center"
        style={{ borderColor: 'rgba(255,255,255,0.07)' }}
      >
        <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>
          © 2026 Shine Jewellery · All Rights Reserved
        </p>
      </div>

    </div>
  );
}
